<script setup lang="ts">
  import { useClipboard } from '@vueuse/core'
  import { computed, onMounted, reactive, useTemplateRef } from 'vue'
  import { IslandPublicView } from '~shared/types/island'

  const props = defineProps<{
    island?: IslandPublicView
  }>()

  const emit = defineEmits(['cancel'])
  const rootEl = useTemplateRef<HTMLDivElement>('rootEl')
  const { copy, copied, isSupported: clipboardSupported, text } = useClipboard()
  const state = reactive({
    shake: true,
    confirmCopyCode: false,
    confirmCopyLink: false,
  })

  const islandUrl = computed(() => window.location.href)
  const islandCode = computed(() => props.island?.customUrlTag || props.island?.turnipCode || '')
  const islandCodeName = computed(() =>
    props.island?.customUrlTag ? 'Custom URL Tag' : 'Turnip Code',
  )

  onMounted(() =>
    rootEl.value?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }),
  )
</script>

<template>
  <div
    class="w-full max-w-2xl rounded-xl border border-red-400/80 bg-purple-500 p-8 text-white shadow-2xl"
    ref="rootEl"
    :class="{ shake: state.shake }"
    @animationend="state.shake = false"
  >
    <div class="flex h-full flex-col">
      <h2 class="font-display mb-2 text-center text-3xl font-bold">Share Your Island</h2>

      <!-- Input Section -->
      <div class="my-12 flex flex-col items-center gap-4 text-center">
        <p>Share your island using this {{ islandCodeName }}:</p>
        <input
          :value="islandCode"
          readonly
          type="text"
          class="w-2/3 rounded-lg border-2 border-white/20 bg-transparent px-4 py-3 text-center text-2xl font-bold tracking-widest uppercase focus:border-red-400 focus:outline-none"
          placeholder="XXX-XXX-XXX"
          maxlength="11"
        />
      </div>

      <!-- Action -->
      <div class="flex justify-center gap-4">
        <button
          v-if="clipboardSupported"
          @click="copy(island?.turnipCode || '')"
          class="cursor-pointer rounded-lg bg-green-400 px-8 py-2 font-bold text-black transition-colors hover:bg-green-500"
        >
          <p v-if="copied && text === islandCode">Copied!</p>
          <p v-else>Copy Code</p>
        </button>

        <button
          v-if="clipboardSupported"
          @click="copy(islandUrl)"
          class="cursor-pointer rounded-lg bg-green-400 px-8 py-2 font-bold text-black transition-colors hover:bg-green-500"
        >
          <p v-if="copied && text === islandUrl">Copied!</p>
          <p v-else>Copy Link</p>
        </button>

        <button
          @click="emit('cancel')"
          class="bg-base-orange cursor-pointer rounded-lg px-8 py-2 font-bold text-black transition-colors hover:bg-red-500"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</template>
